/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
// Store
import { actions } from 'core/store'

// Constants
import { WS_BASE_URL, AI_ASSISTANT_ENDPOINTS } from 'common/constants/apiUrls'
import { PREPROMPTS } from 'common/constants/preprompts'

// Hooks
import { useLocalStorage } from 'core/hooks/storage'

// Styled Elements
import { Wrapper, Container, MessageContainer } from './AICatalystWrapper.elements'
import AICatalystMessage from '../AICatalystMessage'
import AICatalystInput from '../AICatalystInput'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

function AICatalystWrapper(props) {
  // Destructure
  const { model } = props
  // State
  const [messages, setMessages] = useState({})
  const [isNearBottom, setIsNearBottom] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')

  // Refs
  const ws = useRef(null)
  const pendingMsgRef = useRef(null)
  // Hooks
  const [userCredentials] = useLocalStorage('userCredentials', false)

  // Functions
  const handleOpen = (e) => {
    console.log(e)
    console.log('Connected to ospree websocket.')
    // Send initial message to authenticate
    const msg = { id: uuidv4(), token: userCredentials.access_token, context: null, content: '', role: 'user' }
    msg.type = pendingMsgRef.current ? 'reconnect' : 'connect'
    ws.current.send(JSON.stringify(msg))
    setConnectionStatus('connected')
  }

  const handleClose = (e) => {
    console.log(e)
    console.log('Connection has been closed.')
    setConnectionStatus('disconnected')
  }

  const handleError = (e) => {
    console.log(e)
    console.log(`Websocket error: ${e}.`)
    setConnectionStatus('disconnected')
  }

  const handleMessage = (e) => {
    try {
      const data = JSON.parse(e.data)
      // For reconnection
      if (data.type === 'reconnect') {
        const msg = {
          id: uuidv4(),
          token: userCredentials.access_token,
          context: null,
          content: '',
          role: 'user',
          type: 'request',
        }
        const context_messages = []
        Object.keys(messages)?.forEach((key) => {
          context_messages.push(messages[key])
        })
        msg.context = context_messages
        msg.content = pendingMsgRef.current
        setMessages((prevValue) => ({ ...prevValue, [msg.id]: { ...msg, token: null } }))
        pendingMsgRef.current = null
        ws.current.send(JSON.stringify(msg))
        return
      }
      if (data.type === 'ack') {
        setMessages((prevValue) => ({ ...prevValue, [data?.id]: data }))
        return
      }
      if (data.type === 'chunk') {
        setMessages((prevValue) => {
          if (!prevValue[data?.id]) {
            return { ...prevValue, [data?.id]: data }
          }

          return {
            ...prevValue,
            [data?.id]: { ...prevValue[data?.id], content: `${prevValue[data?.id]?.content}${data?.content}` },
          }
        })
        return
      }
      if (data.type === 'response') {
        setIsLoading(false)
        setMessages((prevValue) => ({ ...prevValue, [data?.id]: data }))
      }
    } catch (err) {
      console.error('Error parsing message:', err)
    }
  }

  const connect = useCallback(() => {
    ws.current = new WebSocket(WS_BASE_URL + AI_ASSISTANT_ENDPOINTS?.[model])
    console.log(WS_BASE_URL, AI_ASSISTANT_ENDPOINTS?.[model])

    ws.current.onopen = handleOpen
    ws.current.onmessage = handleMessage
    ws.current.onclose = handleClose
    ws.current.onerror = handleError
    if (ws.current?.readyState === WebSocket.CONNECTING) setConnectionStatus('connecting')
  }, [handleMessage, setConnectionStatus])

  const handleSubmit = (v) => {
    // Handle when disconnected
    if (ws.current?.readyState === WebSocket.OPEN) {
      setIsLoading(true)
      const msg = {
        token: userCredentials.access_token,
        content: v.message,
        role: 'user',
        type: 'request',
        id: uuidv4(),
      }
      setMessages((prevValue) => ({ ...prevValue, [msg.id]: { ...msg, token: null } }))
      ws.current.send(JSON.stringify(msg))
    } else if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
      pendingMsgRef.current = v.message
      connect()
    }
  }

  // UseEffects
  useEffect(() => {
    connect()
    return () => {
      if (ws.current) ws.current.close()
    }
  }, [])

  // Listen for page scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const docHeight = document.documentElement.scrollHeight

      const distanceFromBottom = docHeight - (scrollTop + windowHeight)
      setIsNearBottom(distanceFromBottom <= 60) // within 60px of bottom
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // initialize

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // When new messages come in, scroll if near bottom
  useEffect(() => {
    if (isNearBottom) {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
    }
  }, [messages, isNearBottom])
  useEffect(() => {
    if (!!model && Object.keys(messages).length > 0) window.location.reload()
  }, [model])
  return (
    <Wrapper>
      <Container>
        <MessageContainer>
          {Object.values(messages).map((item) => (
            <AICatalystMessage key={item?.id} message={item} />
          ))}
        </MessageContainer>
        <AICatalystInput
          title={PREPROMPTS?.[model]?.title}
          description={PREPROMPTS?.[model]?.description}
          prompts={PREPROMPTS?.[model]?.preprompts}
          onSubmit={handleSubmit}
          disabled={isLoading}
          centered={Object.keys(messages)?.length < 1}
        />
      </Container>
    </Wrapper>
  )
}

// Default Props
AICatalystWrapper.defaultProps = {
  model: '',
}

// Proptypes Validation
AICatalystWrapper.propTypes = {
  model: PropTypes.string,
}

export default connect(mapStateToProps, mapDispatchToProps)(AICatalystWrapper)

// const sample_messages = [
//   {
//     type: 'request',
//     role: 'user',
//     content: 'Could you explain that in detail?',
//   },
//   {
//     type: 'response',
//     role: 'ai',
//     content: 'The novel 1984 was written by George Orwell',
//   },
// ]
